import React, { useEffect, useRef, useState } from 'react';

import { ReactPlayer as LoadableReactPlayer } from '../../../../utils/loadablePlayers';

interface VideoPlayerProps {
  url: string;
  playing?: boolean;
  loop?: boolean;
  muted?: boolean;
  controls?: boolean;
  width?: string;
  height?: string;
  borderRadius?: string;
  aspectRatio?: string;
  lazy?: boolean;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  url,
  aspectRatio,
  playing = true,
  loop = true,
  muted = true,
  controls = false,
  width = '100%',
  height = '100%',
  borderRadius = '32px',
  lazy = true,
}) => {
  const [isPlaying, setIsPlaying] = useState<boolean>(playing);
  const [videoLoaded, setVideoLoaded] = useState<boolean>(false);
  const [isNearViewport, setIsNearViewport] = useState<boolean>(!lazy);
  const videoRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Lazy loading: defer rendering the player until near viewport
  useEffect(() => {
    if (!lazy) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    const currentContainer = containerRef.current;
    if (currentContainer) {
      observer.observe(currentContainer);
    }

    return () => observer.disconnect();
  }, [lazy]);

  // Play/pause based on visibility for non-looping videos
  useEffect(() => {
    if (!loop) {
      const handleIntersection = (entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsPlaying(true);
          } else {
            setIsPlaying(false);
            if (videoRef.current) {
              videoRef.current.seekTo(0);
            }
          }
        });
      };

      const observer = new IntersectionObserver(handleIntersection, {
        root: null,
        rootMargin: '0px',
        threshold: 0.9,
      });

      const currentContainer = containerRef.current;

      if (currentContainer) {
        observer.observe(currentContainer);
      }

      return () => {
        if (currentContainer) {
          observer.unobserve(currentContainer);
        }
      };
    }
  }, [loop]);

  return (
    <div
      className="video"
      ref={containerRef}
      style={{
        aspectRatio,
        borderRadius,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Skeleton overlay until video is loaded */}
      {!videoLoaded && (
        <div
          className="skeleton skeleton-video"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 2,
          }}
        />
      )}
      {isNearViewport && (
        <LoadableReactPlayer
          ref={videoRef}
          url={url}
          playing={isPlaying}
          loop={loop}
          muted={muted}
          controls={controls}
          width={width}
          height={height}
          playsinline
          config={lazy ? { file: { attributes: { preload: 'none' } } } : undefined}
          onReady={() => setVideoLoaded(true)}
          onEnded={() => {
            if (!loop) {
              setIsPlaying(false);
            }
          }}
          style={{
            opacity: videoLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
        />
      )}
    </div>
  );
};
